import { ConversationResponse } from '@fp/interfaces/conversational/generated/model/conversation-response';
import { CreateConversationRequest } from '@fp/interfaces/conversational/generated/model/create-conversation-request';
import { ConversationModel } from '@klant/conversational-connector/models/Conversation.model';
import { ConversationMapper } from '@klant/conversational-connector/mappers/conversation-mapper/conversation.mapper';

import { Injectable, signal, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { BasePathService } from '../base-path/base-path.service';
import { ChatSession, ConversationalChatVisibility, LoginStatus } from '../../klant-conversational-connector.model';
import { SessionStorageService } from '../../../../session-storage/session-storage.service';
import { ConversationSessionDetails } from '../../../../session-storage/session-storage.model';

@Injectable({ providedIn: 'root' })
export class ConversationService {
    private readonly basePathService = inject(BasePathService);
    private readonly httpClient = inject(HttpClient);
    private readonly sessionStorageService = inject(SessionStorageService);

    private readonly chatVisibility = signal<ConversationalChatVisibility>(null);
    private readonly unreadMessageCounter = signal<number>(0);

    public getChatVisibility = this.chatVisibility.asReadonly();
    public getUnreadMessageCounter = this.unreadMessageCounter.asReadonly();

    public constructor() {
        this.chatVisibility.set(this.sessionStorageService.getChatVisibility());
    }

    public createConversation(conversationsLink: string, chatSession: ChatSession): Observable<ConversationModel> {
        const basePath = this.basePathService.getBasePath();
        let createConversationRequest: CreateConversationRequest = {};

        if (chatSession.loginStatus === LoginStatus.LOGGED_IN) {
            createConversationRequest.token = chatSession.token;
        }

        return this.httpClient
            .post<ConversationResponse>(`${basePath}${conversationsLink}`, createConversationRequest)
            .pipe(map((conversationResponse) => ConversationMapper.mapConversationResponseToConversationModel(conversationResponse)));
    }

    public getConversation(): Observable<ConversationModel> {
        const conversationSessionDetails: ConversationSessionDetails = this.sessionStorageService.getConversationSessionDetails();
        if (!conversationSessionDetails) {
            return throwError(() => new Error('No conversation session details available'));
        }

        const basePath = this.basePathService.getBasePath();
        const conversationLink = conversationSessionDetails.conversationLink;
        const httpHeaderAuthorization = `Bearer ${conversationSessionDetails.accessToken}`;
        const headers = new HttpHeaders().append('Authorization', httpHeaderAuthorization);

        return this.httpClient
            .get<ConversationResponse>(`${basePath}${conversationLink}`, { headers })
            .pipe(map((conversationResponse) => ConversationMapper.mapConversationResponseToConversationModel(conversationResponse)));
    }

    public setConversationVisibility(chatVisibility: ConversationalChatVisibility): void {
        this.chatVisibility.set(chatVisibility);
        this.sessionStorageService.updateChatVisibility(chatVisibility);
    }

    public updateUnreadMessageCount(count: number): void {
        this.unreadMessageCounter.set(count);
    }
}
